import { AnyEventObject, EventData, interpret, State, StateConfig } from 'xstate';
import VueRouter from 'vue-router';
import prouniStateMachine, { ProuniContext } from './stateMachine';
import stepRouteMapping from './stepRouteMapping';

/**
 * Independent ProUni pipeline interpreter. Kept fully separate from the
 * enrollment pipeline singleton (`src/pipeline/index.ts`) so the two flows
 * never collide.
 */
const prouniPipeline = interpret(prouniStateMachine);
const transitionListeners: Function[] = [];

function getCurrentStep(state: State<ProuniContext>): string {
  const [currentStateMeta]: { step: string }[] = Object.values(state.meta);
  return currentStateMeta.step;
}

function navigateToStep(router: VueRouter, state: State<ProuniContext>) {
  const currentStep = getCurrentStep(state);
  const stepRouteName = stepRouteMapping[currentStep];
  const { name: currentRoute, query } = router.currentRoute;

  if (stepRouteName && stepRouteName !== currentRoute) {
    router.push({ name: stepRouteName, query }).catch(() => undefined);
  }
}

function loadPreviousState(
  previousState: StateConfig<ProuniContext, AnyEventObject>,
): State<ProuniContext> {
  const state = State.create<ProuniContext>(previousState);
  return prouniStateMachine.resolveState(state);
}

function onTransitionHandler(router: VueRouter) {
  return (state: State<ProuniContext>) => {
    if (state.changed) {
      navigateToStep(router, state);
      transitionListeners.forEach(cb => cb(state));
    }
  };
}

function start(router: VueRouter, previousState?: StateConfig<ProuniContext, AnyEventObject>) {
  const loadedPreviousState = previousState ? loadPreviousState(previousState) : undefined;

  const { state: currentState } = prouniPipeline
    .onTransition(onTransitionHandler(router))
    .start(loadedPreviousState);

  navigateToStep(router, currentState);
}

function addTransitionListener(listener: Function, forceTrigger?: boolean) {
  const isAlreadyRegistered = transitionListeners.includes(listener);

  if (!isAlreadyRegistered) {
    if (forceTrigger) listener(prouniPipeline.state);
    transitionListeners.push(listener);
  }
}

function removeTransitionListener(cb: Function) {
  const listenerIndex = transitionListeners.indexOf(cb);
  if (listenerIndex >= 0) transitionListeners.splice(listenerIndex, 1);
}

export default {
  get currentStep() {
    return getCurrentStep(prouniPipeline.state);
  },
  get state() {
    return prouniPipeline.state;
  },
  get initialized() {
    return prouniPipeline.initialized;
  },
  addTransitionListener,
  removeTransitionListener,
  goTo: (step: string) => {
    if (step) prouniPipeline.send(`GO_TO_${step.toUpperCase()}`);
  },
  loadPreviousState,
  navigateToStep,
  next: (event?: EventData) => prouniPipeline.send('NEXT', event),
  prev: (event?: EventData) => prouniPipeline.send('PREV', event),
  send: prouniPipeline.send,
  start,
  stop: prouniPipeline.stop,
  transitionListeners,
  __pipeline: prouniPipeline,
};
